/**
 * Tiny event bus living on `window.__lensHostStatus` that lets React
 * components subscribe to connection state from a lens-host adapter that
 * was installed before React mounted (e.g. the auto-installed
 * `installLensHostWebSocket` in lens-renderer's App.jsx).
 *
 * Shape:
 *   window.__lensHostStatus.state          { status, fatalReason }
 *   window.__lensHostStatus.addEventListener("change", () => ...)
 *
 * `status` is one of "idle" | "connecting" | "open" | "closed".
 * `fatalReason` is a human-readable string when the adapter received a
 * fatal close (displaced, policy violation, invalid message), null
 * otherwise. Once fatalReason is set, the adapter stops reconnecting.
 *
 * Adapters that don't have a meaningful connection state (e.g.
 * installLensHostPostMessage) simply never publish to this bus, in which
 * case React subscribers see the default "idle" forever.
 */
/**
 * @typedef {"idle" | "connecting" | "open" | "closed"} LensHostStatus
 *
 * @typedef {Object} LensHostStatusState
 * @property {LensHostStatus} status
 * @property {string | null} fatalReason
 *
 * @typedef {EventTarget & { state: LensHostStatusState }} LensHostStatusBus
 */
const GLOBAL_KEY = "__lensHostStatus";
/**
 * Get-or-create the singleton bus on `window`.
 * @returns {LensHostStatusBus | null}
 */
export function getLensHostStatusBus() {
    if (typeof window === "undefined")
        return null;
    if (!window[GLOBAL_KEY]) {
        const target = new EventTarget();
        target.state = { status: "idle", fatalReason: null };
        window[GLOBAL_KEY] = target;
    }
    return window[GLOBAL_KEY] ?? null;
}
/**
 * @param {LensHostStatus} status
 */
export function publishLensHostStatus(status) {
    const bus = getLensHostStatusBus();
    if (!bus)
        return;
    bus.state = { ...bus.state, status };
    bus.dispatchEvent(new CustomEvent("change"));
}
/**
 * @param {string} reason
 */
export function publishLensHostFatal(reason) {
    const bus = getLensHostStatusBus();
    if (!bus)
        return;
    bus.state = { ...bus.state, fatalReason: reason };
    bus.dispatchEvent(new CustomEvent("change"));
}
//# sourceMappingURL=lensHostStatusBus.js.map